/* eslint-disable @next/next/no-img-element */
'use client'

import {useCallback, useEffect, useMemo, useRef, useState} from 'react'
import {ArrowLeft, CalendarDays, Clock3, MapPin, RotateCcw, UserRound, UsersRound} from 'lucide-react'
import {formatEventSessionDisplay} from '@fitro/date-time/display'
import {addCalendarDays} from '@fitro/date-time'
import {PublicScheduleDayPicker} from '../client/public-entry/PublicScheduleDayPicker'
import {PublicScheduleWeekControlsView} from '../client/public-entry/PublicScheduleWeekControls'
import {Avatar} from '../ui/Avatar'
import {Button} from '@fitro/customer-ui/Button'
import {SectionAccordion} from '@fitro/customer-ui/SectionAccordion'
import {SanitizedRichContent} from '../ui/SanitizedRichContent'
import {getLocalWeekRange, shiftLocalWeekRange, type LocalWeekRange} from '../client/public-entry/publicEntryDates'
import {buildAuthHref, withEmbedSource} from '../client/public-entry/publicEntryLinks'
import {getPublicEventBookingHandoffHref, getPublicEventBookingMode, getPublicEventDetailsHref} from '../client/public-entry/publicEventLinks'
import {resolvePublicAssetUrl} from '../client/public-entry/publicAssetUrl'
import {publicScheduleStyles} from '../client/public-entry/publicEntryStyles'
import {formatInstructorNames} from '../../lib/utils'
import {POLISH_PUBLIC_DATE_TIME as DATE_TIME, polishPublicDateTimeInZone} from '../../lib/polish-public-date-time'
import {getClassColorAccentToken} from '../../lib/classColors'
import type {PublicEmbedConfig} from '../../lib/publicEmbedConfig'
import {getPublicPartnerProfileStrict} from '../../services/public-profile.service'
import {getPublicPartnerEventsStrict, type PublicScheduleAvailabilityStatus, type PublicScheduleEvent} from '../../services/public-schedule.service'
import {PublicEmbedResizeBridge} from './PublicEmbedResizeBridge'
import {EventPriceRow} from '../client/public-entry/EventPriceRow'
import {publicEventPrice, type PublicEventPrice} from '../client/public-entry/publicEventPrice'

type LoadState<T> =
    | {status: 'loading'}
    | {status: 'error'}
    | {status: 'success'; data: T}

const AVAILABILITY_LABELS: Record<PublicScheduleAvailabilityStatus, string> = {
    available: 'Dostępne miejsca',
    last_spots: 'Ostatnie miejsca',
    full: 'Brak miejsc',
    registration_closed: 'Zapisy zamknięte',
    occurred: 'Zakończone',
}

function weekDays(range: LocalWeekRange): string[] {
    return Array.from({length: 7}, (_, index) => addCalendarDays(range.startDate, index))
}

function eventDateKey(event: PublicScheduleEvent): string | null {
    return eventFormatter(event).dateKeyInZone(event.startDateTime)
}

function eventFormatter(event: PublicScheduleEvent) {
    return polishPublicDateTimeInZone(event.timeZone)
}

function canBook(event: PublicScheduleEvent): boolean {
    return event.bookingOpen !== false
        && (event.availabilityStatus === 'available' || event.availabilityStatus === 'last_spots')
}

function eventTitle(event: PublicScheduleEvent): string | null {
    const title = event.className?.trim()
    return title || null
}

function instructorLabel(event: PublicScheduleEvent): string | null {
    return formatInstructorNames(event.instructors, '').trim() || null
}

function instructorAvatars(event: PublicScheduleEvent): Array<{name: string; src: string}> {
    return event.instructors.flatMap((instructor) => {
        const name = [instructor.firstName, instructor.lastName].filter(Boolean).join(' ').trim()
        const src = resolvePublicAssetUrl(instructor.avatarUrl)
        return name && src ? [{name, src}] : []
    })
}

type EmbedDisplay = Pick<PublicEmbedConfig, 'prices' | 'instructors'>

// The embed has no i18n providers; the copy mirrors landing.json publicEntry.events and client.price.lowest30d.
const PRICE_LABELS = {
    free: 'Bezpłatne',
    passesOnly: 'Tylko z karnetem',
    lowest30d: (price: string) => `Najniższa cena z 30 dni przed obniżką: ${price}`,
    firstEntry: (price: string) => `Pierwsza wizyta ${price}`,
    srPrefix: 'Cena: ',
}

/** Schedule tiles show a price only with prices "all"; event cards and details with "events" too. */
function shownPrice(event: PublicScheduleEvent, display: EmbedDisplay, place: 'schedule' | 'events'): PublicEventPrice | null {
    if (event.availabilityStatus === 'occurred' || display.prices === 'none') return null
    if (place === 'schedule' && display.prices !== 'all') return null
    return publicEventPrice(event)
}

function PriceRow({price, compact}: {price: PublicEventPrice | null; compact: boolean}) {
    return price ? <EventPriceRow price={price} compact={compact} emphasis={!compact} locale="pl-PL" labels={PRICE_LABELS} /> : null
}

const SPOTS_PLURAL = new Intl.PluralRules('pl-PL')

function availabilityLabel(event: PublicScheduleEvent): string {
    const spots = event.spotsLeft
    const bookable = event.availabilityStatus === 'available' || event.availabilityStatus === 'last_spots'
    if (!bookable || typeof spots !== 'number') return AVAILABILITY_LABELS[event.availabilityStatus]
    if (spots === 0) return AVAILABILITY_LABELS.full
    const form = SPOTS_PLURAL.select(spots)
    if (form === 'one') return '1 wolne miejsce'
    return form === 'few' ? `${spots} wolne miejsca` : `${spots} wolnych miejsc`
}

function PoweredByFitro() {
    return (
        <footer className="mt-auto flex items-center justify-center gap-1.5 pt-4 text-xs font-medium text-slate-500">
            <span>Powered by</span>
            <a href="https://fitro.pl/?utm_source=studio&utm_medium=powered_by&utm_campaign=widget" target="_blank" rel="noreferrer" aria-label="Fitro">
                <img src="/landing/logo.svg" alt="Fitro" width="44" height="14" className="h-3.5 w-auto opacity-70" />
            </a>
        </footer>
    )
}

function LoadingState({label}: {label: string}) {
    return (
        <div className="rounded-2xl border border-slate-100 bg-white px-5 py-8 text-center text-sm text-slate-500" role="status">
            {label}
        </div>
    )
}

function ErrorState({onRetry, title = 'Chwilowo niedostępne'}: {onRetry: () => void; title?: string}) {
    return (
        <div className="rounded-2xl border border-error-100 bg-error-50/50 px-5 py-7 text-center">
            <p className="font-semibold text-slate-800">{title}</p>
            <button
                type="button"
                onClick={onRetry}
                className="mt-3 inline-flex min-h-11 items-center justify-center gap-2 rounded-xl border border-slate-200 bg-white px-4 text-sm font-semibold text-slate-700 shadow-sm transition-colors hover:bg-slate-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500"
            >
                <RotateCcw aria-hidden="true" className="h-4 w-4" />
                Spróbuj ponownie
            </button>
        </div>
    )
}

function Availability({event}: {event: PublicScheduleEvent}) {
    const tone = event.spotsLeft === 0 ? 'text-slate-500'
        : event.availabilityStatus === 'available' ? 'text-success-700'
            : event.availabilityStatus === 'last_spots' ? 'text-amber-700' : 'text-slate-500'
    return (
        <span className={`inline-flex items-center gap-1.5 text-xs font-semibold ${tone}`}>
            <UsersRound aria-hidden="true" className="h-4 w-4 shrink-0" />
            {availabilityLabel(event)}
        </span>
    )
}

function ReserveAction({event, href}: {event: PublicScheduleEvent; href: string | null}) {
    // A finished class keeps only its "Zakończone" status; a dead button adds noise.
    if (event.availabilityStatus === 'occurred') return null
    if (!href || !canBook(event)) {
        return (
            <Button
                type="button"
                disabled
                variant="ghost"
                size="small"
                className={`${publicScheduleStyles.actionBase} mt-auto min-h-10 w-full px-4 text-sm font-semibold disabled:pointer-events-auto disabled:cursor-not-allowed disabled:bg-slate-100 disabled:text-slate-400 disabled:hover:bg-slate-100 disabled:hover:text-slate-400 disabled:active:scale-100 sm:min-h-10`}
            >
                Zapisz się
            </Button>
        )
    }
    return (
        <a
            href={withEmbedSource(href)}
            target="_top"
            className={`${publicScheduleStyles.actionBase} ${publicScheduleStyles.actionPrimary} min-h-10 px-4 text-sm font-semibold`}
        >
            Zapisz się
        </a>
    )
}

// The embed cannot see the visitor's session: a Term a guest can book opens its page, where the visitor
// books without an account or signs in; any other Term goes straight to signing in.
function scheduleReserveHref(event: PublicScheduleEvent): string {
    const guestHref = getPublicEventBookingMode(event, false) === 'guest' ? getPublicEventDetailsHref(event) : null
    return guestHref ?? buildAuthHref('/sign-in', getPublicEventBookingHandoffHref(event, '/calendar'))
}

function ScheduleCard({event, display}: {event: PublicScheduleEvent; display: EmbedDisplay}) {
    const title = eventTitle(event)
    if (!title) return null
    const formatter = eventFormatter(event)
    const instructor = display.instructors !== 'hide' ? instructorLabel(event) : null
    const avatars = instructorAvatars(event)
    const location = event.location?.name?.trim() || null
    const occurred = event.availabilityStatus === 'occurred'
    const href = scheduleReserveHref(event)

    return (
        <article className={`flex h-full min-w-0 flex-col rounded-xl border px-3 py-2.5 ${occurred ? 'border-slate-100 bg-slate-50 shadow-none' : 'border-slate-200 bg-white shadow-[0_8px_24px_rgba(15,23,42,0.05)]'}`}>
            <p className={`flex items-center gap-1.5 text-xs font-semibold ${occurred ? 'text-slate-500' : 'text-primary-700'}`}>
                <Clock3 aria-hidden="true" className="h-4 w-4" />
                {formatter.formatInstantTimeRange(event.startDateTime, event.endDateTime)}
            </p>
            <h3 title={title} className={`mt-1.5 line-clamp-2 break-words text-sm font-bold leading-snug ${occurred ? 'text-slate-500' : 'text-slate-900'}`}>{title}</h3>
            <div className={`mt-2 space-y-1.5 text-xs ${occurred ? 'text-slate-500' : 'text-slate-600'}`}>
                {instructor ? (
                    <div className="flex min-w-0 items-center gap-1.5">
                        {avatars.length > 0 ? (
                            <span className="flex shrink-0 -space-x-1" aria-hidden="true">
                                {avatars.map(avatar => <Avatar key={`${avatar.name}-${avatar.src}`} name={avatar.name} src={avatar.src} size="small" className={`h-4 w-4 text-[7px] ring-1 ring-white ${occurred ? 'grayscale' : ''}`} aria-hidden="true" />)}
                            </span>
                        ) : <UserRound aria-hidden="true" className="h-3.5 w-3.5 shrink-0" />}
                        <span className="truncate">{instructor}</span>
                    </div>
                ) : null}
                {location ? <p className="flex min-w-0 items-center gap-1.5"><MapPin aria-hidden="true" className="h-3.5 w-3.5 shrink-0" /><span className="truncate">{location}</span></p> : null}
                <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
                    <Availability event={event} />
                    <PriceRow price={shownPrice(event, display, 'schedule')} compact />
                </div>
            </div>
            <div className="mt-auto pt-3"><ReserveAction event={event} href={href} /></div>
        </article>
    )
}

function AgendaScheduleRow({event, display}: {event: PublicScheduleEvent; display: EmbedDisplay}) {
    const title = eventTitle(event)
    if (!title) return null
    const formatter = eventFormatter(event)
    const instructor = display.instructors !== 'hide' ? instructorLabel(event) : null
    const location = event.location?.name?.trim() || null
    const occurred = event.availabilityStatus === 'occurred'
    const href = scheduleReserveHref(event)

    return (
        <article
            className={`grid gap-3 border-l-4 border-l-primary-500 px-4 py-4 sm:grid-cols-[104px_minmax(0,1fr)_15rem] sm:items-center ${occurred ? 'bg-slate-50 text-slate-500' : 'bg-white'}`}
            style={event.classColorKey ? {borderLeftColor: getClassColorAccentToken(event.classColorKey)} : undefined}
        >
            <p className={`flex items-center gap-1.5 text-sm font-semibold ${occurred ? 'text-slate-500' : 'text-primary-700'}`}>
                <Clock3 aria-hidden="true" className="h-4 w-4" />
                {formatter.formatInstantTimeRange(event.startDateTime, event.endDateTime)}
            </p>
            <div className="min-w-0">
                <h4 className={`break-words text-sm font-bold ${occurred ? 'text-slate-600' : 'text-slate-900'}`}>{title}</h4>
                {instructor || location ? (
                    <div className="mt-1.5 space-y-1 text-xs text-slate-500">
                        {instructor ? <p className="flex items-start gap-1.5"><UserRound aria-hidden="true" className="mt-0.5 h-3.5 w-3.5 shrink-0" /><span className="min-w-0 break-words">{instructor}</span></p> : null}
                        {location ? <p className="flex items-start gap-1.5"><MapPin aria-hidden="true" className="mt-0.5 h-3.5 w-3.5 shrink-0" /><span className="min-w-0 break-words">{location}</span></p> : null}
                    </div>
                ) : null}
            </div>
            {/* A fixed action column keeps every row's button the same width, whatever the price shows. */}
            <div className="flex flex-col gap-2">
                <div className="flex flex-wrap items-start gap-x-3 gap-y-1 sm:justify-end">
                    <Availability event={event} />
                    <PriceRow price={shownPrice(event, display, 'schedule')} compact />
                </div>
                <ReserveAction event={event} href={href} />
            </div>
        </article>
    )
}

function EventCard({event, display, onOpenDetails}: {event: PublicScheduleEvent; display: EmbedDisplay; onOpenDetails: (trigger: HTMLButtonElement) => void}) {
    const title = eventTitle(event)
    const href = getPublicEventDetailsHref(event)
    if (!title || !href) return null
    const formatter = eventFormatter(event)
    const session = formatEventSessionDisplay(formatter, new Date(event.startDateTime), new Date(event.endDateTime))
    const instructor = display.instructors !== 'hide' ? instructorLabel(event) : null
    const avatars = instructorAvatars(event)
    const location = event.location?.name?.trim() || null
    const description = event.description?.trim() || null
    const price = shownPrice(event, display, 'events')
    const imageUrl = resolvePublicAssetUrl(event.classCoverImageUrl)
    const occurred = event.availabilityStatus === 'occurred'

    return (
        <article className={`group relative overflow-hidden rounded-2xl border ${occurred ? 'border-slate-100 bg-slate-50 shadow-none' : 'border-slate-200 bg-white shadow-[0_12px_32px_rgba(15,23,42,0.06)]'}`}>
            <button type="button" onClick={clickEvent => onOpenDetails(clickEvent.currentTarget)} aria-label={`Zobacz szczegóły wydarzenia ${title}`} className="absolute inset-0 z-0 cursor-pointer focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-primary-500" />
            <div className="pointer-events-none relative z-[1] flex h-full flex-col">
                {imageUrl ? (
                    <div className={`aspect-[16/9] overflow-hidden bg-slate-100 ${occurred ? 'grayscale' : ''}`}>
                        <img src={imageUrl} alt="" className="h-full w-full object-cover" onError={(event) => { event.currentTarget.hidden = true }} />
                    </div>
                ) : null}
                <div className="flex h-full flex-col p-4">
                    <p className={`flex flex-wrap items-start gap-x-1.5 gap-y-0.5 text-xs font-semibold ${occurred ? 'text-slate-500' : 'text-primary-700'}`}>
                        <CalendarDays aria-hidden="true" className="mt-0.5 h-4 w-4 shrink-0" />
                        <span>{session.primaryLabel}</span>
                        <span>{session.isCrossDay ? session.secondaryPrefix : '· '}{session.secondaryLabel}</span>
                    </p>
                    <h3 className={`mt-2 break-words text-lg font-bold leading-tight ${occurred ? 'text-slate-500' : 'text-slate-900'}`}>{title}</h3>
                    {description ? <p className="mt-1.5 line-clamp-2 text-sm leading-5 text-slate-500">{description}</p> : null}
                    <div className={`mt-3 space-y-2 text-sm ${occurred ? 'text-slate-500' : 'text-slate-600'}`}>
                        {instructor ? (
                            <div className="flex items-center gap-2">
                                {avatars.length > 0 ? (
                                    <span className="flex shrink-0 -space-x-1" aria-hidden="true">
                                        {avatars.map(avatar => <Avatar key={`${avatar.name}-${avatar.src}`} name={avatar.name} src={avatar.src} size="small" className={`h-5 w-5 text-[8px] ring-1 ring-white ${occurred ? 'grayscale' : ''}`} aria-hidden="true" />)}
                                    </span>
                                ) : <UserRound aria-hidden="true" className="h-4 w-4 shrink-0" />}
                                <span>{instructor}</span>
                            </div>
                        ) : null}
                        {location ? <p className="flex items-start gap-2"><MapPin aria-hidden="true" className="mt-0.5 h-4 w-4 shrink-0" /><span>{location}</span></p> : null}
                        <Availability event={event} />
                        <PriceRow price={price} compact={false} />
                    </div>
                    <div className="pointer-events-auto relative z-[2] mt-auto pt-4"><ReserveAction event={event} href={href} /></div>
                </div>
            </div>
        </article>
    )
}

function EventDetailsView({event, display, onBack}: {event: PublicScheduleEvent; display: EmbedDisplay; onBack: () => void}) {
    const [rulesOpen, setRulesOpen] = useState(false)
    const title = eventTitle(event)
    const href = getPublicEventDetailsHref(event)
    if (!title || !href) return null
    const formatter = eventFormatter(event)
    const session = formatEventSessionDisplay(formatter, new Date(event.startDateTime), new Date(event.endDateTime))
    const instructor = display.instructors !== 'hide' ? instructorLabel(event) : null
    const avatars = instructorAvatars(event)
    const location = event.location?.name?.trim() || null
    const description = event.description?.trim() || null
    const fullDescriptionHtml = event.fullDescriptionHtml?.trim() || null
    const fullDescription = event.fullDescription?.trim() || null
    const eventRulesHtml = event.eventRulesHtml?.trim() || null
    const eventRules = event.eventRules?.trim() || null
    const price = shownPrice(event, display, 'events')
    const imageUrl = resolvePublicAssetUrl(event.classCoverImageUrl)
    const occurred = event.availabilityStatus === 'occurred'

    return (
        <section data-testid="public-embed-event-details" aria-labelledby="public-embed-event-details-title" className="pb-2">
            <div className="sticky top-0 z-20 mb-3 bg-slate-50/95 pb-1 backdrop-blur-sm">
                <button type="button" autoFocus onClick={onBack} className="inline-flex min-h-11 items-center gap-2 rounded-lg px-1 text-sm font-semibold text-primary-700 hover:text-primary-800 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500">
                    <ArrowLeft aria-hidden="true" className="h-4 w-4" />
                    Wróć do wydarzeń
                </button>
            </div>
            <article className={`grid items-start gap-5 rounded-2xl border border-slate-200 bg-white p-4 shadow-[0_12px_32px_rgba(15,23,42,0.06)] sm:p-6 lg:gap-7 ${imageUrl ? 'lg:grid-cols-[minmax(0,3fr)_minmax(320px,2fr)]' : ''}`}>
                {imageUrl ? (
                    <div className={`aspect-[16/10] overflow-hidden rounded-xl bg-slate-100 ${occurred ? 'grayscale' : ''}`}>
                        <img src={imageUrl} alt="" className="h-full w-full object-cover" onError={(imageEvent) => { imageEvent.currentTarget.hidden = true }} />
                    </div>
                ) : null}
                <div className="min-w-0 lg:py-2">
                    <p className={`flex flex-wrap items-start gap-x-1.5 gap-y-0.5 text-sm font-semibold ${occurred ? 'text-slate-500' : 'text-primary-700'}`}>
                        <CalendarDays aria-hidden="true" className="mt-0.5 h-4 w-4 shrink-0" />
                        <span>{session.primaryLabel}</span>
                        <span>{session.isCrossDay ? session.secondaryPrefix : '· '}{session.secondaryLabel}</span>
                    </p>
                    <h2 id="public-embed-event-details-title" className={`mt-2 break-words text-2xl font-bold leading-tight sm:text-3xl ${occurred ? 'text-slate-500' : 'text-slate-900'}`}>{title}</h2>
                    {description ? <p className="mt-3 whitespace-pre-line text-sm leading-6 text-slate-600">{description}</p> : null}
                    <div className={`mt-5 grid gap-3 text-sm sm:grid-cols-2 ${occurred ? 'text-slate-500' : 'text-slate-600'}`}>
                        {instructor ? (
                            <div className="flex items-center gap-2">
                                {avatars.length > 0 ? (
                                    <span className="flex shrink-0 -space-x-1" aria-hidden="true">
                                        {avatars.map(avatar => <Avatar key={`${avatar.name}-${avatar.src}`} name={avatar.name} src={avatar.src} size="small" className={`h-7 w-7 text-[9px] ring-2 ring-white ${occurred ? 'grayscale' : ''}`} aria-hidden="true" />)}
                                    </span>
                                ) : <UserRound aria-hidden="true" className="h-5 w-5 shrink-0" />}
                                <span>{instructor}</span>
                            </div>
                        ) : null}
                        {location ? <p className="flex items-center gap-2"><MapPin aria-hidden="true" className="h-5 w-5 shrink-0" /><span>{location}</span></p> : null}
                        <Availability event={event} />
                        <PriceRow price={price} compact={false} />
                    </div>
                    <div className="mt-6"><ReserveAction event={event} href={href} /></div>
                </div>
            </article>
            {fullDescriptionHtml || fullDescription ? (
                <section aria-labelledby="public-embed-event-description-title" className="mt-6 rounded-2xl border border-slate-200 bg-white p-5 sm:p-6">
                    <h3 id="public-embed-event-description-title" className="mb-3 text-lg font-bold text-slate-900">Szczegóły wydarzenia</h3>
                    {fullDescriptionHtml
                        ? <SanitizedRichContent html={fullDescriptionHtml} className="text-slate-600 dark:text-slate-600" />
                        : <p className="whitespace-pre-line text-sm leading-6 text-slate-600">{fullDescription}</p>}
                </section>
            ) : null}
            {eventRulesHtml || eventRules ? (
                <div className="mt-4"><SectionAccordion title="Regulamin wydarzenia" isOpen={rulesOpen} onToggle={() => setRulesOpen(open => !open)} >
                    {eventRulesHtml
                        ? <SanitizedRichContent html={eventRulesHtml} className="text-slate-600 dark:text-slate-600" />
                        : <p className="whitespace-pre-line text-sm leading-6 text-slate-600">{eventRules}</p>}
                </SectionAccordion></div>
            ) : null}
        </section>
    )
}

type SectionOutcome = 'loading' | 'empty' | 'content' | 'error'
type ScheduleView = 'day' | 'week'

function ScheduleSection({
    slug,
    studioTimeZone,
    scheduleLayout,
    display,
    weekIndex,
    combinedEmpty,
    onNext,
    onPrevious,
    onWeekChange,
    onOutcome,
}: {
    slug: string
    studioTimeZone: string
    scheduleLayout: PublicEmbedConfig['scheduleLayout']
    display: EmbedDisplay
    weekIndex: number
    combinedEmpty: boolean
    onNext: () => void
    onPrevious: () => void
    onWeekChange: (weekIndex: number) => void
    onOutcome: (outcome: SectionOutcome) => void
}) {
    const dateTime = useMemo(() => polishPublicDateTimeInZone(studioTimeZone), [studioTimeZone])
    const weekRange = useMemo(() => shiftLocalWeekRange(dateTime, getLocalWeekRange(dateTime), weekIndex), [dateTime, weekIndex])
    const days = useMemo(() => weekDays(weekRange), [weekRange])
    const todayKey = dateTime.today()
    const [state, setState] = useState<LoadState<PublicScheduleEvent[]>>({status: 'loading'})
    const [view, setView] = useState<ScheduleView>('week')
    const [selectedDay, setSelectedDay] = useState(() => days.includes(todayKey) ? todayKey : (days[0] ?? weekRange.startDate))
    const requestId = useRef(0)
    const weekScrollerRef = useRef<HTMLDivElement>(null)

    const load = useCallback(async () => {
        const currentRequest = ++requestId.current
        setState({status: 'loading'})
        onOutcome('loading')
        try {
            const page = await getPublicPartnerEventsStrict(slug, {
                isCyclical: true,
                startDate: weekRange.startDate,
                endDate: weekRange.endDate,
            })
            if (currentRequest !== requestId.current) return
            const events = page.content.filter(event => event.isCyclical && eventTitle(event))
            setState({status: 'success', data: events})
            onOutcome(events.length === 0 ? 'empty' : 'content')
        } catch {
            if (currentRequest !== requestId.current) return
            setState({status: 'error'})
            onOutcome('error')
        }
    }, [onOutcome, slug, weekRange.endDate, weekRange.startDate])

    useEffect(() => {
        void load()
        return () => { requestId.current += 1 }
    }, [load])

    const events = state.status === 'success' ? state.data : []
    const byDay = (day: string) => events.filter(event => eventDateKey(event) === day)
    const compactRange = DATE_TIME.formatLocalDateRange(weekRange.startDate, weekRange.endDate)
    const readableRange = DATE_TIME.formatLocalDateRange(weekRange.startDate, weekRange.endDate, {style: 'long'})
    const selectedDayEvents = byDay(selectedDay)
    const baseWeekRange = getLocalWeekRange(dateTime)
    const maxWeekStart = shiftLocalWeekRange(dateTime, baseWeekRange, 3).startDate

    useEffect(() => {
        setSelectedDay(current => days.includes(current) ? current : (days.includes(todayKey) ? todayKey : (days[0] ?? weekRange.startDate)))
    }, [days, todayKey, weekRange.startDate])

    const selectWeek = (weekStart: string) => {
        const nextWeekIndex = Array.from({length: 4}, (_, index) => shiftLocalWeekRange(dateTime, baseWeekRange, index).startDate).indexOf(weekStart)
        if (nextWeekIndex >= 0) onWeekChange(nextWeekIndex)
    }

    useEffect(() => {
        const scroller = weekScrollerRef.current
        if (scheduleLayout !== 'calendar' || state.status !== 'success' || view !== 'week' || !scroller || scroller.scrollWidth <= scroller.clientWidth) return
        if (weekIndex !== 0) {
            scroller.scrollLeft = 0
            return
        }

        const todayColumn = scroller.querySelector<HTMLElement>(`[data-day="${todayKey}"]`)
        if (!todayColumn) return

        const maxScrollLeft = scroller.scrollWidth - scroller.clientWidth
        scroller.scrollLeft = Math.max(0, Math.min(
            maxScrollLeft,
            todayColumn.offsetLeft - (scroller.clientWidth - todayColumn.offsetWidth) / 2,
        ))
    }, [scheduleLayout, state.status, todayKey, view, weekIndex])

    return (
        <section aria-label="Grafik" className="min-w-0">
            <h2 className="sr-only">Grafik</h2>
            <PublicScheduleWeekControlsView
                inlinePicker
                alwaysShowViewSwitcher={scheduleLayout === 'calendar'}
                canGoNext={weekIndex < 3}
                canGoPrevious={weekIndex > 0}
                compactLabel={compactRange}
                maxWeekStart={maxWeekStart}
                readableLabel={readableRange}
                selectedDateKey={view === 'day' ? selectedDay : null}
                selectedWeekStart={weekRange.startDate}
                todayKey={todayKey}
                view={view}
                dateTime={dateTime}
                labels={{
                    close: 'Zamknij',
                    day: 'Dzień',
                    nextMonth: 'Następny miesiąc',
                    nextWeek: 'Następny tydzień',
                    previousMonth: 'Poprzedni miesiąc',
                    previousWeek: 'Poprzedni tydzień',
                    selectPeriod: 'Wybierz okres',
                    view: 'Widok grafiku',
                    week: 'Tydzień',
                }}
                hideViewSwitcher={scheduleLayout === 'agenda'}
                onNext={onNext}
                onPrevious={onPrevious}
                onSelectDate={(dateKey) => {
                    if (view === 'day') setSelectedDay(dateKey)
                }}
                onSelectWeek={selectWeek}
                onViewChange={setView}
            />

            {state.status === 'loading' ? <LoadingState label="Ładowanie grafiku…" /> : null}
            {state.status === 'error' ? <ErrorState onRetry={load} title="Grafik jest obecnie niedostępny" /> : null}
            {state.status === 'success' && combinedEmpty ? (
                <div className="rounded-2xl border border-slate-200 bg-white px-5 py-10 text-center">
                    <p className="font-semibold text-slate-700">Brak aktualnego grafiku i wydarzeń</p>
                </div>
            ) : null}
            {state.status === 'success' && !combinedEmpty ? (
                <>
                    {scheduleLayout === 'calendar' && view === 'day' ? (
                        <div aria-label="Wybór dnia" role="group">
                            <PublicScheduleDayPicker
                                days={days.map(day => ({key: day}))}
                                formatDayMonth={(dateKey) => DATE_TIME.formatLocalDate(dateKey, {style: 'dayMonth'})}
                                formatDayNumber={DATE_TIME.formatLocalDateDay}
                                formatFullWeekday={(dateKey) => DATE_TIME.formatLocalDateWeekday(dateKey, {width: 'long', context: 'sentence'})}
                                formatMobileWeekday={(dateKey) => DATE_TIME.formatLocalDateWeekday(dateKey, {width: 'short', context: 'sentence'})}
                                isDayDisabled={day => weekIndex === 0 && day.key < todayKey}
                                onSelectDay={setSelectedDay}
                                selectedDateKey={selectedDay}
                                todayKey={todayKey}
                            />
                        </div>
                    ) : null}
                    <div className="relative mt-3 overflow-hidden rounded-xl">
                        <div
                            aria-label={scheduleLayout === 'agenda' ? 'Grafik tygodniowy, dni jeden pod drugim' : view === 'week' ? 'Grafik tygodniowy, przewiń poziomo' : 'Grafik wybranego dnia'}
                            className={scheduleLayout === 'calendar' && view === 'week'
                                ? 'max-w-full overflow-x-auto overscroll-x-contain pb-3 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 [scrollbar-width:thin]'
                                : 'max-w-full pb-3'}
                            data-testid="public-embed-schedule-scroller"
                            ref={weekScrollerRef}
                            role="region"
                            tabIndex={scheduleLayout === 'calendar' && view === 'week' ? 0 : undefined}
                        >
                            {scheduleLayout === 'agenda' ? (
                                <div className="space-y-3 p-0.5 pr-2" data-testid="public-embed-agenda">
                                    {events.length > 0 ? days.map(day => {
                                        const dayEvents = byDay(day)
                                        return (
                                            <section key={day} className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm" data-testid="public-embed-agenda-day">
                                                <h3 className={`px-4 py-3 text-sm font-bold ${day === todayKey ? 'border-t-2 border-primary-500 bg-primary-50 text-primary-800' : 'bg-slate-50 text-slate-800'}`}>
                                                    {DATE_TIME.formatLocalDateWithWeekday(day, {
                                                        dateStyle: 'long',
                                                        weekday: 'long',
                                                        context: 'sentence',
                                                        includeYear: true,
                                                    })}
                                                </h3>
                                                {dayEvents.length > 0 ? (
                                                    <div className="divide-y divide-slate-100">
                                                        {dayEvents.map(event => <AgendaScheduleRow key={event.id} event={event} display={display} />)}
                                                    </div>
                                                ) : (
                                                    <p className="px-4 py-5 text-sm text-slate-500">Brak zajęć</p>
                                                )}
                                            </section>
                                        )
                                    }) : null}
                                </div>
                            ) : view === 'week' ? (
                                <div className="grid min-w-[1190px] grid-cols-7 gap-2 lg:min-w-0" data-testid="public-embed-week-grid">
                                    {days.map(day => {
                                        const dayEvents = byDay(day)
                                        return (
                                            <div key={day} className="min-w-0" data-day={day}>
                                                <div className={`mb-2 rounded-xl px-2 py-2 text-center ${day === todayKey ? 'bg-primary-50 text-primary-700 shadow-[inset_0_2px_0_var(--color-primary-500)]' : 'bg-white text-slate-600'}`}>
                                                    <span className="block text-[11px] font-semibold capitalize">{DATE_TIME.formatLocalDateWeekday(day, {width: 'short'})}</span>
                                                    <span className="block text-sm font-bold">{DATE_TIME.formatLocalDateDay(day)}</span>
                                                </div>
                                                <div className="space-y-2">
                                                    {dayEvents.map(event => <ScheduleCard key={event.id} event={event} display={display} />)}
                                                    {events.length > 0 && dayEvents.length === 0 ? <p className="rounded-xl bg-white px-2 py-3 text-center text-[11px] text-slate-500">Brak zajęć</p> : null}
                                                </div>
                                            </div>
                                        )
                                    })}
                                </div>
                            ) : selectedDayEvents.length > 0 ? (
                                <div className="space-y-2 p-0.5 pr-2">
                                    {selectedDayEvents.map(event => <ScheduleCard key={event.id} event={event} display={display} />)}
                                </div>
                            ) : (
                                <p className="rounded-2xl bg-white px-5 py-7 text-center text-sm text-slate-500">Brak zajęć w wybranym dniu</p>
                            )}
                        </div>
                    </div>
                    {events.length === 0 ? <p className="mt-3 rounded-2xl bg-white px-5 py-7 text-center text-sm text-slate-500">Brak zajęć w tym tygodniu</p> : null}
                </>
            ) : null}
        </section>
    )
}

function EventsSection({
    slug,
    display,
    onOpenDetails,
    onOutcome,
    showHeading,
    showEmpty,
}: {
    slug: string
    display: EmbedDisplay
    onOpenDetails: (event: PublicScheduleEvent, trigger: HTMLButtonElement) => void
    onOutcome: (outcome: SectionOutcome) => void
    showHeading: boolean
    showEmpty: boolean
}) {
    const [state, setState] = useState<LoadState<PublicScheduleEvent[]>>({status: 'loading'})
    const requestId = useRef(0)
    const load = useCallback(async () => {
        const currentRequest = ++requestId.current
        setState({status: 'loading'})
        onOutcome('loading')
        try {
            const page = await getPublicPartnerEventsStrict(slug, {isCyclical: false})
            if (currentRequest !== requestId.current) return
            const events = page.content
                .filter(event => !event.isCyclical && eventTitle(event) && getPublicEventDetailsHref(event))
                .sort((left, right) => Date.parse(left.startDateTime) - Date.parse(right.startDateTime))
            setState({status: 'success', data: events})
            onOutcome(events.length === 0 ? 'empty' : 'content')
        } catch {
            if (currentRequest !== requestId.current) return
            setState({status: 'error'})
            onOutcome('error')
        }
    }, [onOutcome, slug])

    useEffect(() => {
        void load()
        return () => { requestId.current += 1 }
    }, [load])

    if (state.status === 'success' && state.data.length === 0 && !showEmpty) return null

    return (
        <section aria-label="Wydarzenia" className={showHeading ? 'mt-8' : ''}>
            <h2 className={showHeading ? 'mb-4 text-xl font-bold text-slate-900' : 'sr-only'}>Wydarzenia</h2>
            {state.status === 'loading' ? <LoadingState label="Ładowanie wydarzeń…" /> : null}
            {state.status === 'error' ? <ErrorState onRetry={load} title="Wydarzenia są obecnie niedostępne" /> : null}
            {state.status === 'success' && state.data.length === 0 ? (
                <div className="rounded-2xl border border-slate-200 bg-white px-5 py-10 text-center">
                    <p className="font-semibold text-slate-700">Brak wydarzeń</p>
                </div>
            ) : null}
            {state.status === 'success' && state.data.length > 0 ? (
                <>
                    <div className="grid items-start gap-4 sm:grid-cols-2 lg:grid-cols-3">
                        {state.data.slice(0, 3).map(event => <EventCard key={event.id} event={event} display={display} onOpenDetails={trigger => onOpenDetails(event, trigger)} />)}
                    </div>
                    {state.data.length > 3 ? (
                        <a href={withEmbedSource('/wydarzenia')} target="_top" className="mt-5 inline-flex min-h-11 items-center font-semibold text-primary-700 hover:text-primary-800 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500">
                            Zobacz wszystkie wydarzenia
                        </a>
                    ) : null}
                </>
            ) : null}
        </section>
    )
}

function EnabledEmbed({config, slug, studioTimeZone}: {config: PublicEmbedConfig; slug: string; studioTimeZone: string}) {
    const [weekIndex, setWeekIndex] = useState(0)
    const [scheduleOutcome, setScheduleOutcome] = useState<SectionOutcome>('loading')
    const [eventsOutcome, setEventsOutcome] = useState<SectionOutcome>('loading')
    const [activeSection, setActiveSection] = useState<'schedule' | 'events'>('schedule')
    const [selectedEvent, setSelectedEvent] = useState<PublicScheduleEvent | null>(null)
    const scheduleTabRef = useRef<HTMLButtonElement>(null)
    const eventsTabRef = useRef<HTMLButtonElement>(null)
    const eventTriggerRef = useRef<HTMLButtonElement | null>(null)
    const showSchedule = config.sections !== 'events'
    const showEvents = config.sections !== 'schedule'
    const showTabs = config.layout === 'tabs' && showSchedule && showEvents
    const bothEmpty = showSchedule && showEvents && scheduleOutcome === 'empty' && eventsOutcome === 'empty'
    const renderAsTabs = showTabs && !bothEmpty

    useEffect(() => {
        if (!selectedEvent) eventTriggerRef.current?.focus()
    }, [selectedEvent])

    return (
        <>
            {selectedEvent ? <EventDetailsView event={selectedEvent} display={config} onBack={() => setSelectedEvent(null)} /> : null}
            <div data-testid="public-embed-overview" hidden={selectedEvent !== null}>
            {renderAsTabs ? (
                <div className="mb-6 inline-flex rounded-xl border border-slate-200 bg-white p-1" role="tablist" aria-label="Widok embeda">
                    {(['schedule', 'events'] as const).map(section => {
                        const selected = activeSection === section
                        const label = section === 'schedule' ? 'Grafik' : 'Wydarzenia'
                        return (
                            <button
                                key={section}
                                type="button"
                                role="tab"
                                aria-controls={`public-embed-${section}-panel`}
                                aria-selected={selected}
                                id={`public-embed-${section}-tab`}
                                onClick={() => setActiveSection(section)}
                                onKeyDown={event => {
                                    let target: 'schedule' | 'events' | null = null
                                    if (event.key === 'ArrowLeft' || event.key === 'ArrowRight') target = section === 'schedule' ? 'events' : 'schedule'
                                    if (event.key === 'Home') target = 'schedule'
                                    if (event.key === 'End') target = 'events'
                                    if (!target) return

                                    event.preventDefault()
                                    setActiveSection(target)
                                    const targetRef = target === 'schedule' ? scheduleTabRef : eventsTabRef
                                    targetRef.current?.focus()
                                }}
                                ref={section === 'schedule' ? scheduleTabRef : eventsTabRef}
                                tabIndex={selected ? 0 : -1}
                                className={`min-h-10 rounded-lg px-4 text-sm font-semibold transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 ${selected ? 'bg-slate-900 text-white shadow-sm' : 'text-slate-700 hover:bg-slate-50 hover:text-slate-900'}`}
                            >
                                {label}
                            </button>
                        )
                    })}
                </div>
            ) : null}
            {showSchedule ? (
                <div
                    aria-labelledby={renderAsTabs ? 'public-embed-schedule-tab' : undefined}
                    data-testid="public-embed-schedule-panel"
                    hidden={renderAsTabs && activeSection !== 'schedule'}
                    id="public-embed-schedule-panel"
                    role={renderAsTabs ? 'tabpanel' : undefined}
                >
                    <ScheduleSection
                        slug={slug}
                        studioTimeZone={studioTimeZone}
                        scheduleLayout={config.scheduleLayout}
                        display={config}
                        weekIndex={weekIndex}
                        combinedEmpty={bothEmpty}
                        onPrevious={() => setWeekIndex(index => Math.max(0, index - 1))}
                        onNext={() => setWeekIndex(index => Math.min(3, index + 1))}
                        onWeekChange={setWeekIndex}
                        onOutcome={setScheduleOutcome}
                    />
                </div>
            ) : null}
            {showEvents ? (
                <div
                    aria-labelledby={renderAsTabs ? 'public-embed-events-tab' : undefined}
                    data-testid="public-embed-events-panel"
                    hidden={renderAsTabs && activeSection !== 'events'}
                    id="public-embed-events-panel"
                    role={renderAsTabs ? 'tabpanel' : undefined}
                >
                    <EventsSection
                        slug={slug}
                        display={config}
                        onOpenDetails={(event, trigger) => {
                            eventTriggerRef.current = trigger
                            setSelectedEvent(event)
                        }}
                        onOutcome={setEventsOutcome}
                        showHeading={!showTabs}
                        showEmpty={config.sections === 'events' || (showTabs && !bothEmpty)}
                    />
                </div>
            ) : null}
            </div>
        </>
    )
}

export function PublicScheduleEventsEmbed({
    config = {layout: 'stacked', sections: 'all', scheduleLayout: 'calendar', accent: 'fitro', surface: 'soft', spots: 'label', prices: 'events', instructors: 'show'},
    configurationError = false,
    slug,
}: {
    config?: PublicEmbedConfig
    configurationError?: boolean
    slug: string | null
}) {
    const [availability, setAvailability] = useState<LoadState<{timeZone: string} | null>>({status: 'loading'})
    const availabilityRequestId = useRef(0)

    const loadAvailability = useCallback(async () => {
        if (configurationError) return
        const currentRequest = ++availabilityRequestId.current
        if (!slug) {
            setAvailability({status: 'success', data: null})
            return
        }

        setAvailability({status: 'loading'})
        try {
            const profile = await getPublicPartnerProfileStrict(slug)
            if (currentRequest === availabilityRequestId.current) {
                setAvailability({status: 'success', data: profile?.active === true ? {timeZone: profile.timeZone} : null})
            }
        } catch {
            if (currentRequest === availabilityRequestId.current) setAvailability({status: 'error'})
        }
    }, [configurationError, slug])

    useEffect(() => {
        void loadAvailability()
        return () => { availabilityRequestId.current += 1 }
    }, [loadAvailability])

    return (
        <main
            data-public-embed-root
            data-embed-accent={config.accent}
            data-embed-surface={config.surface}
            className="fitro-public-root flex min-h-[320px] flex-col bg-slate-50 px-3 py-4 text-slate-900 sm:px-5 sm:py-6"
        >
            <h1 className="sr-only">Grafik i wydarzenia</h1>
            <div className="mx-auto flex w-full flex-1 flex-col max-w-6xl">
                {configurationError ? <ErrorState onRetry={() => window.location.reload()} /> : null}
                {!configurationError && availability.status === 'loading' ? <LoadingState label="Ładowanie…" /> : null}
                {!configurationError && availability.status === 'error' ? <ErrorState onRetry={loadAvailability} /> : null}
                {!configurationError && availability.status === 'success' && !availability.data ? (
                    <div className="rounded-2xl border border-slate-200 bg-white px-5 py-10 text-center">
                        <p className="font-semibold text-slate-700">Grafik jest obecnie niedostępny</p>
                    </div>
                ) : null}
                {!configurationError && availability.status === 'success' && availability.data && slug ? <EnabledEmbed config={config} slug={slug} studioTimeZone={availability.data.timeZone} /> : null}
                <PoweredByFitro />
            </div>
            <PublicEmbedResizeBridge />
        </main>
    )
}
